import type {
    HeroStats,
    RawHeroStats,
    HeroHistory,
    HeroHistorySnapshot,
    HistoryTuple,
    StatsData,
    MapHistoryData,
} from "$lib/types";
import { assignTiers as _assignTiers } from "./tiers";
import { isPlusTier, computeWeightedRates } from "./ranks";
import { isInsufficientData } from "./formatting";
export { PICK_RATE_WEIGHT, DEMAND_WEIGHT, INSUFFICIENT_DATA_TIER } from "./tiers";
export type { TierInput, TierResult } from "./tiers";

// ---------------------------------------------------------------------------
// Tier assignment – re-export with concrete OW types
// ---------------------------------------------------------------------------

export function assignTiers(heroes: RawHeroStats[]): HeroStats[] {
    return _assignTiers(heroes);
}

// ---------------------------------------------------------------------------
// History helpers
// ---------------------------------------------------------------------------

export interface TrendDataPoint {
    date: string;
    winRate: number;
    pickRate: number;
    /**
     * Ban rate at this snapshot. `null` for legacy snapshots predating
     * ban-rate tracking (so consumers can distinguish "never banned" from
     * "no data recorded").
     */
    banRate: number | null;
}

/** Extract BR from a HistoryTuple, returning null for legacy 2-tuples. */
function tupleBr(t: HistoryTuple): number | null {
    return t.length === 3 ? t[2] : null;
}

/**
 * Extract trend data for a hero from their history file.
 * Returns an array of { date, winRate, pickRate, banRate } sorted oldest → newest.
 *
 * Reads from the specified combo sub-key (e.g. "PC/Americas"), with
 * fallback for legacy flat-format snapshots.
 *
 * Supports "+" tier keys (e.g. "Gold+") via weighted averaging across the
 * included individual tiers using approximate rank distribution weights.
 */
export function getHeroTrendData(
    history: HeroHistory,
    tier: string,
    mode: "competitive" | "quickplay",
    combo: string = "PC/Americas",
): TrendDataPoint[] {
    const points: TrendDataPoint[] = [];
    const isPlusMode = mode === "competitive" && isPlusTier(tier);

    for (const snap of history.snapshots) {
        let winRate: number;
        let pickRate: number;
        let banRate: number | null;

        const comboData = snap[combo] as
            | { competitive?: Record<string, HistoryTuple>; quickplay?: HistoryTuple }
            | undefined;
        const competitive =
            comboData?.competitive ?? (snap as unknown as { competitive?: Record<string, HistoryTuple> }).competitive;
        const quickplay = comboData?.quickplay ?? (snap as unknown as { quickplay?: HistoryTuple }).quickplay;

        if (isPlusMode) {
            // Weighted average across the included tiers
            const rates = computeWeightedRates(competitive ?? {}, tier);
            if (!rates) continue;
            [winRate, pickRate, banRate] = rates;
        } else if (mode === "competitive") {
            const tuple = competitive?.[tier];
            if (!tuple) continue;
            winRate = tuple[0];
            pickRate = tuple[1];
            banRate = tupleBr(tuple);
        } else {
            if (!quickplay) continue;
            winRate = quickplay[0];
            pickRate = quickplay[1];
            banRate = tupleBr(quickplay);
        }

        // Skip pre-release placeholder data (sentinel value -0.01 = hero not yet in the game)
        if (isInsufficientData(winRate) || isInsufficientData(pickRate)) continue;

        points.push({ date: snap.date, winRate, pickRate, banRate });
    }

    points.sort((a, b) => a.date.localeCompare(b.date));
    return points;
}

// ---------------------------------------------------------------------------
// Historical patch reconstruction
// ---------------------------------------------------------------------------

/** Build a RawHeroStats row from hero metadata and a snapshot tuple. */
function rowFromTuple(hero: RawHeroStats, tuple: HistoryTuple): RawHeroStats {
    return {
        name: hero.name,
        slug: hero.slug,
        role: hero.role,
        subrole: hero.subrole,
        winRate: tuple[0],
        pickRate: tuple[1],
        banRate: tupleBr(tuple),
    };
}

/**
 * Pick the snapshot that best represents a hero's stats during a patch period
 * [patchDate, endDate). Falls back to the latest snapshot before the period
 * end; returns undefined when the hero has no snapshot that early (didn't exist).
 */
function snapshotForPeriod(history: HeroHistory, patchDate: string, endDate: string): HeroHistorySnapshot | undefined {
    const sorted = [...history.snapshots].sort((a, b) => a.date.localeCompare(b.date));
    const inRange = sorted.filter((s) => s.date >= patchDate && s.date < endDate);
    if (inRange.length) return inRange[inRange.length - 1];
    const beforeEnd = sorted.filter((s) => s.date < endDate);
    return beforeEnd.length ? beforeEnd[beforeEnd.length - 1] : undefined;
}

/**
 * Fetch every hero's history file for the given stats roster.
 * Heroes whose file is missing or fails to load are simply omitted.
 */
export async function fetchHeroHistories(
    stats: StatsData,
    fetchFn: typeof fetch = fetch,
): Promise<Map<string, HeroHistory>> {
    const roster = stats.data.competitive["All"]?.["all"] ?? [];
    const results = await Promise.all(
        roster.map(async (hero) => {
            try {
                const res = await fetchFn(`/history/heroes/${hero.slug}.json`);
                if (res.ok) return [hero.slug, (await res.json()) as HeroHistory] as const;
            } catch {
                /* missing history file — skip */
            }
            return null;
        }),
    );
    const map = new Map<string, HeroHistory>();
    for (const r of results) if (r) map.set(r[0], r[1]);
    return map;
}

/** Resolve the end (exclusive) of a patch period from newest-first patch dates. */
export function patchPeriodEnd(orderedDates: string[], patchDate: string): string {
    const idx = orderedDates.findIndex((d) => d === patchDate);
    return idx > 0 ? orderedDates[idx - 1] : "9999-12-31";
}

/**
 * Reconstruct a StatsData snapshot for a historical patch period from cached
 * hero histories.
 *
 * Only the aggregate ("all" maps) rosters exist in history, so the result fills
 * `data.competitive[tier]["all"]` for every base rank tier plus `quickplay["all"]`
 * and leaves `maps` empty. "+" tiers need no special handling — `getHeroData`
 * derives them from the base tiers via weighting.
 */
export function reconstructStatsData(
    histories: Map<string, HeroHistory>,
    combo: string,
    patchDate: string,
    endDate: string,
    baseStats: StatsData,
): StatsData {
    const tierKeys = baseStats.tiers.map((t) => t.key);
    const competitive: Record<string, Record<string, RawHeroStats[]>> = {};
    for (const key of tierKeys) competitive[key] = { all: [] };
    const quickplay: Record<string, RawHeroStats[]> = { all: [] };

    const roster = baseStats.data.competitive["All"]?.["all"] ?? [];
    for (const hero of roster) {
        const history = histories.get(hero.slug);
        if (!history?.snapshots.length) continue;

        const snap = snapshotForPeriod(history, patchDate, endDate);
        if (!snap) continue;

        const comboData = (snap[combo] ?? snap) as {
            competitive?: Record<string, HistoryTuple>;
            quickplay?: HistoryTuple;
        };

        const comp = comboData?.competitive;
        if (comp) {
            for (const key of tierKeys) {
                const tuple = comp[key];
                if (tuple) competitive[key].all.push(rowFromTuple(hero, tuple));
            }
        }

        const qp = comboData?.quickplay;
        if (qp) quickplay.all.push(rowFromTuple(hero, qp));
    }

    return {
        fetchedAt: baseStats.fetchedAt,
        maps: [],
        tiers: baseStats.tiers,
        data: { competitive, quickplay },
    };
}

/**
 * Wrap a frozen map-history snapshot (`StatsData["data"]`) as a full StatsData,
 * borrowing `tiers`/`maps` from the current stats. Used to drive per-map tables
 * and charts at a historical patch directly from the precomputed snapshot.
 */
export function statsFromMapHistory(data: MapHistoryData, baseStats: StatsData): StatsData {
    return {
        fetchedAt: baseStats.fetchedAt,
        maps: baseStats.maps,
        tiers: baseStats.tiers,
        data,
    };
}
